import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Grid
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import moment from 'moment';
import { GetApp, ArrowBack } from '@material-ui/icons';
import FilterListIcon from '@material-ui/icons/FilterList';
import Skeleton from 'react-loading-skeleton';
import useReport from '../useReport';
import EmailCampaignDetail from './EmailCampaignDetail';
import { NavBar, TemplateSingleSelect } from 'components';
import Activity from './Activity';
import { StatBoxCard } from 'views/Reports/components';
import DateFilterPopover from 'views/Reports/components/DateFilterPopover';
import { convertToLocalTimeWhileIgnoringOffset } from 'helpers/timeUtils';
import { getEnvVar } from 'helpers';
import { updateFilterTags } from 'store/helpers/tableAndToolbar/actions';
import { switchCampaignReportData, getCampaignReportCsvFile } from 'store/reports/actions';
import { getAdvancedSearchPaginatedCampaigns } from 'store/campaign/actions';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4)
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  metric: {
    color: theme.palette.primary.main
  },
  divide: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  selectables: {
    marginRight: theme.spacing(2)
  },
  select: {
    fontSize: '14px'
  },
  subContainer: {
    display: 'flex',
    alignItem: 'center'
  },
  providerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: theme.spacing(1)
  },
  navBar: {
    display: 'flex',
    marginTop: theme.spacing(1)
  },
  dateField: {
    marginRight: theme.spacing(1)
  },
  message: {
    textAlign: 'center'
  },
  downloadGrid: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  menu: {
    fontSize: '20px',
    paddingTop: '0px',
    paddingLeft: '0px',
    marginLeft: '0px',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(6)
  },
  campaignSelect: {
    width: 220,
    marginRight: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: theme.palette.common.white
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: '8px',
      paddingBottom: '8px',
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 10px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  },
  campaignSelectPanel: {
    marginTop: theme.spacing(1),
    width: 560,
    maxWidth: '78vw'
  },
  persistentCard: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  persistentCardContent: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1)
    }
  },
  rightControls: {
    alignItems: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  dateTypeToggleGroup: {
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      padding: '4px 14px'
    }
  },
  outsideDateTypeToggle: {
    marginRight: theme.spacing(1)
  }
}));

const Campaign = props => {
  const { match } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { campaignUuid, type, providerMetric } = useParams();
  const history = useHistory();
  const [selectedMetric, setSelectedMetric] = useState(providerMetric || null);
  const isActivityMode = Boolean(providerMetric || selectedMetric);
  const report = useReport({
    shouldFetchUnsubscribeCount: type === 'EMAIL' && !isActivityMode,
    isActivityView: isActivityMode,
    ignoreCampaignListUpdates: true
  });

  const campaignName = useSelector(store => store.reports.campaignName);
  const emptyMessage = useSelector(store => store.reports.data);
  const {
    campaignData,
    queryData,
    updateParams,
    applyFilters,
    handleDateChange,
    onFocus,
    onBlur,
    shouldShowError,
    getErrorMessage,
    isLoading,
    isDownloading,
    onClickDownloadCsv,
    getMaxAvailableDailyDate,
    getMinAvailableDailyDate,
    getMaxAvailableMonthlyDate,
    getMinAvailableMonthlyDate,
    getPresetRange,
    getMatchingPreset,
    dailyReportDelayHours,
    monthlyReportDelayHours,
    dailyRuntime,
    monthlyRuntime
  } = report;

  const currentCampaigns = useSelector(store => store.campaign.advancedPaginatedList);
  const isCampaignFilterLoading = useSelector(store => store.campaign.gettingPaginated);
  const filteredTags = useSelector(store => store.tableAndToolbar.filterTags);
  const businessContextId = useSelector(store => store.user.selectedBusinessContext?.businessContextId);
  const contextDetails = useSelector(store => store.user.contextDetails || []);
  const isAllContext = businessContextId === 'Default';

  const [filterOpen, setFilterOpen] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [campaignSearchByValues, setCampaignSearchByValues] = useState(['property']);
  const [campaignTypeFilter, setCampaignTypeFilter] = useState(type || 'EMAIL');
  const [smsProviderFilter, setSmsProviderFilter] = useState('VONAGE');
  const [campaignPromotionalFilter, setCampaignPromotionalFilter] = useState('all');
  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null);
  const [dateFilterError, setDateFilterError] = useState('');
  const [dateFilterDraft, setDateFilterDraft] = useState({
    rangePreset: 'LAST_7_DAYS',
    fromDate: '',
    toDate: ''
  });
  const [rangeSelectionStep, setRangeSelectionStep] = useState('start');
  const [visibleDailyMonth, setVisibleDailyMonth] = useState(moment().startOf('month'));
  const [visibleMonthYear, setVisibleMonthYear] = useState(moment().year());
  const campaignId = queryData?.campaignId;
  const activeProviderMetric =
    selectedMetric || providerMetric || queryData?.providerMetric || null;
  const isActivityView = Boolean(activeProviderMetric || providerMetric);
  const isDateFilterOpen = Boolean(dateFilterAnchorEl);

  useEffect(() => {
    setAllCampaigns([...currentCampaigns]);
  }, [currentCampaigns]);

  useEffect(() => {
    setCampaignSearchByValues(isAllContext ? ['property'] : []);
  }, [isAllContext]);

  useEffect(() => {
    setCampaignTypeFilter(type || queryData.type || 'EMAIL');
  }, [type, queryData.type]);

  useEffect(() => {
    setSmsProviderFilter(queryData.providerType || 'VONAGE');
  }, [queryData.providerType]);

  const getCampaignSearchScopes = (searchByValues = campaignSearchByValues) => {
    if (!isAllContext) {
      return [];
    }

    if (Array.isArray(searchByValues) && searchByValues.length > 0) {
      if (searchByValues.includes('property')) {
        return null;
      }
      if (searchByValues.includes('tenantOnly')) {
        return ['tenantOnly'];
      }
    }
    return null;
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('isFilterOpen') === "true") {
      setFilterOpen(true);
      const url = new URL(window.location);
      url.searchParams.delete('isFilterOpen');
      window.history.pushState({}, '', url);
    }
  }, []);

  useEffect(() => {
    dispatch(updateFilterTags(campaignUuid));
  }, []);

  const handleProviderTypeToggle = (event, value) => {
    if (!value || value === (queryData.providerType || 'VONAGE')) return;
    setSmsProviderFilter(value);
    updateParams('providerType', value);
  };

  const openDateFilter = event => {
    // Try to find which preset matches the current date range
    const matchingPreset = getMatchingPreset(queryData.fromDate, queryData.toDate, queryData.dateType);
    
    const nextDraft = {
      rangePreset: matchingPreset || (queryData.dateType === 'MONTH' ? 'LAST_24_MONTHS' : 'LAST_60_DAYS'),
      fromDate: queryData.fromDate || '',
      toDate: queryData.toDate || ''
    };
    setDateFilterError('');
    setDateFilterDraft(nextDraft);
    setRangeSelectionStep('start');
    setVisibleDailyMonth(moment(nextDraft.toDate || nextDraft.fromDate || getMaxAvailableDailyDate()).startOf('month'));
    setVisibleMonthYear(moment(nextDraft.toDate || nextDraft.fromDate || getMaxAvailableMonthlyDate()).year());
    setDateFilterAnchorEl(event.currentTarget);
  };

  const closeDateFilter = () => {
    setDateFilterError('');
    setDateFilterAnchorEl(null);
  };

  const applyDateFilters = () => {
    const fromDate = moment(dateFilterDraft.fromDate);
    const toDate = moment(dateFilterDraft.toDate);
    const effectiveDateType = isActivityView ? 'DAY' : (queryData.dateType || 'DAY');

    if (!dateFilterDraft.fromDate || !dateFilterDraft.toDate) {
      setDateFilterError('Please select both start and end dates');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      setDateFilterError('Start date cannot be after end date');
      return;
    }

    const nextQueryData = { ...queryData, dateType: effectiveDateType };

    if (effectiveDateType === 'DAY') {
      nextQueryData.fromDate = fromDate.format('YYYY-MM-DD');
      nextQueryData.toDate = toDate.format('YYYY-MM-DD');
    }

    if (effectiveDateType === 'MONTH') {
      const fromMonth = moment(dateFilterDraft.fromDate).startOf('month');
      const toMonth = moment(dateFilterDraft.toDate).endOf('month');
      const [minMonth, maxMonth] = fromMonth.isBefore(toMonth) ? [fromMonth, toMonth] : [toMonth, fromMonth];
      nextQueryData.fromDate = minMonth.format('YYYY-MM-DD');
      nextQueryData.toDate = maxMonth.format('YYYY-MM-DD');
    }

    applyFilters({
      dateType: isActivityView ? 'DAY' : nextQueryData.dateType,
      fromDate: nextQueryData.fromDate,
      toDate: nextQueryData.toDate
    });
    closeDateFilter();
  };

  const handleDateTypeChange = (event, value) => {
    if (!value || value === queryData.dateType) return;
    if (isActivityView && value === 'MONTH') return;

    if (isDateFilterOpen) {
      const defaultPreset = value === 'MONTH' ? 'LAST_24_MONTHS' : 'LAST_60_DAYS';
      const range = getPresetRange(defaultPreset, value);
      setDateFilterDraft({ rangePreset: defaultPreset, fromDate: range.fromDate, toDate: range.toDate });
      setRangeSelectionStep('start');
      setVisibleMonthYear(moment(range.toDate).year());
      if (value === 'DAY') setVisibleDailyMonth(moment(range.toDate).startOf('month'));
    }
    updateParams('dateType', value);
  };

  const getDateFilterButtonLabel = () => {
    if (!isActivityView && queryData.dateType === 'MONTH') {
      const fromMonth = queryData.fromDate ? moment(queryData.fromDate).format('MM-YYYY') : '--';
      const toMonth = queryData.toDate ? moment(queryData.toDate).format('MM-YYYY') : '--';
      return `monthly (${fromMonth}-${toMonth})`;
    }
    const fromDate = queryData.fromDate ? moment(queryData.fromDate).format('MM/DD/YYYY') : '--';
    const toDate = queryData.toDate ? moment(queryData.toDate).format('MM/DD/YYYY') : '--';
    return `daily (${fromDate}-${toDate})`;
  };

  const handleMetricCsvDownload = () => {
    dispatch(
      getCampaignReportCsvFile({
        ...queryData,
        campaignId,
        campaignName,
        campaignUuid,
        type,
        providerMetric: activeProviderMetric
      })
    );
  };

  const handleStatClick = metric => {
    history.push(`/reports/campaigns/${campaignUuid}/${type}/activity/${metric}`);
  };

  const handleBackToDashboard = () => {
    history.push(`/reports/campaigns/${campaignUuid}/${type}/details`);
  };

  const expirationDays =
    parseInt(getEnvVar('ADM_REPORT_EXPIRATION_DAYS')) || 60;
  const configuredExpirationMonthsInDays = parseInt(
    getEnvVar('ADM_REPORT_EXPIRATION_MONTHS_IN_DAYS'),
    10
  );
  const expirationMonthsInDays = Number.isFinite(configuredExpirationMonthsInDays)
    ? Math.max(configuredExpirationMonthsInDays, 730)
    : 730;

  const navBarOptions = {
    SMS: [
      // {
      //   name: 'Summary',
      //   go: `/reports/campaigns/${campaignUuid}/${type}/details`
      // }
    ],
    EMAIL: []
  };

  const invalidDate = (startDate, endDate) => {
    let date = new Date();
    date.setDate(date.getDate() - expirationMonthsInDays);
    return new Date(endDate) < date || new Date(startDate) >= new Date();
  };
  
  const contextLookup = React.useMemo(() => {
    const lookup = {};
    (contextDetails || []).forEach(context => {
      lookup[context.businessContextId] = context.name;
    });
    lookup.Default = lookup.Default || 'ALL';
    return lookup;
  }, [contextDetails]);

  const selectedCampaignValue = React.useMemo(() => {
    if (Array.isArray(filteredTags)) {
      return filteredTags[0] || '';
    }
    return filteredTags || '';
  }, [filteredTags]);

  const shouldShowCampaignContext = React.useMemo(
    () => isAllContext && Array.isArray(campaignSearchByValues) && campaignSearchByValues.includes('tenantOnly'),
    [campaignSearchByValues, isAllContext]
  );

  const campaignDropdownOptions = React.useMemo(
    () =>
      (allCampaigns || [])
        .slice()
        .filter(campaign => {
          if (campaign.type !== campaignTypeFilter) return false;
          if (campaignPromotionalFilter === 'promotional' && !campaign.promotional) return false;
          if (campaignPromotionalFilter === 'nonPromotional' && campaign.promotional) return false;
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(campaign => {
          const contextName = contextLookup[campaign.businessContextId] || campaign.businessContextId;
          const isFutureCampaign = new Date(campaign.startDate) >= new Date();
          const expiredCampaign = (() => { const d = new Date(); d.setDate(d.getDate() - expirationMonthsInDays); return new Date(campaign.endDate) < d; })();
          const isArchivedCampaign = campaign.active === false;
          const showArchivedExpirationTooltip = isArchivedCampaign && expiredCampaign;
          const tooltipLines = [campaign.name];
          if (isFutureCampaign) {
            tooltipLines.push(`${campaign.name} has not started yet`);
          } else if (showArchivedExpirationTooltip) {
            tooltipLines.push(`ended more than ${expirationMonthsInDays} days ago`);
          }
          return {
            uuid: campaign.uuid,
            name: campaign.name,
            type: campaign.type,
            disabled: invalidDate(campaign.startDate, campaign.endDate),
            toolTip: tooltipLines.join('\n'),
            secondaryText: shouldShowCampaignContext ? `Context: ${contextName}` : '',
            searchText: shouldShowCampaignContext ? `${campaign.name} ${contextName}` : campaign.name
          };
        }),
    [
      allCampaigns,
      contextLookup,
      expirationMonthsInDays,
      shouldShowCampaignContext,
      campaignTypeFilter,
      campaignPromotionalFilter
    ]
  );

  const onFilterChange = selectedCampaign => {
    const value = selectedCampaign?.value || '';
    dispatch(updateFilterTags(value));
    dispatch(switchCampaignReportData({ value, type: selectedCampaign?.type, openFilter: false }));
  };

  const handleCampaignSelectionChange = event => {
    const selectedValue = event?.target?.value || '';
    const selectedCampaign = campaignDropdownOptions.find(option => option.uuid === selectedValue);
    onFilterChange(selectedCampaign ? { value: selectedCampaign.uuid, type: selectedCampaign.type } : { value: '' });
  };

  const handleCampaignStatusChange = newStatus => {
    setCampaignStatusFilter(newStatus);
    let activeFilter = null;
    if (newStatus === 'active') activeFilter = true;
    else if (newStatus === 'archived') activeFilter = false;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignSearch = searchTerm => {
    let activeFilter = null;
    if (campaignStatusFilter === 'active') activeFilter = true;
    else if (campaignStatusFilter === 'archived') activeFilter = false;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      searchScopes: getCampaignSearchScopes(),
      searchName: searchTerm,
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignSearchByChange = (newValues) => {
    setCampaignSearchByValues(newValues);
    let activeFilter = null;
    if (campaignStatusFilter === 'active') activeFilter = true;
    else if (campaignStatusFilter === 'archived') activeFilter = false;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      searchScopes: getCampaignSearchScopes(newValues),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignDropdownOpen = () => {
    let activeFilter = null;
    if (campaignStatusFilter === 'active') activeFilter = true;
    else if (campaignStatusFilter === 'archived') activeFilter = false;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleSmsProviderChange = newProvider => {
    setSmsProviderFilter(newProvider);
    updateParams('providerType', newProvider);
  };

  const handlePromotionalFilterChange = newValue => {
    setCampaignPromotionalFilter(newValue);
    let activeFilter = null;
    if (campaignStatusFilter === 'active') activeFilter = true;
    else if (campaignStatusFilter === 'archived') activeFilter = false;
    const promotionalFilter = newValue === 'promotional' ? true : newValue === 'nonPromotional' ? false : null;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter
    }));
  };

  // Clear selected campaign when campaign type filter changes
  useEffect(() => {
    if (selectedCampaignValue) {
      const selectedCampaign = (allCampaigns || []).find(c => c.uuid === selectedCampaignValue);
      if (selectedCampaign && selectedCampaign.type !== campaignTypeFilter) {
        onFilterChange({ value: '' });
      }
    }
  }, [campaignTypeFilter, selectedCampaignValue, allCampaigns]);

  const campaignStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' }
  ];

  const campaignSearchByOptions = [
    { value: 'tenantOnly', label: 'Tenant' },
    { value: 'property', label: 'Property' }
  ];

  const smsProviderOptions = [
    { value: 'VONAGE', label: 'Vonage' },
    { value: 'TWILIO', label: 'Twilio' }
  ];

  const promotionalFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'nonPromotional', label: 'Non-Promotional' }
  ];

  return (
    <div className={classes.root}>
      <Card className={classes.persistentCard}>
        <CardContent className={classes.persistentCardContent}>
          <Grid className={classes.subContainer}>
            <Grid className={classes.navBar}>
              <TemplateSingleSelect
                label="Campaigns"
                name="campaignId"
                value={selectedCampaignValue}
                onChange={handleCampaignSelectionChange}
                options={{ list: campaignDropdownOptions, valueId: 'uuid' }}
                fullWidth={false}
                margin="none"
                dataTestId="campaign-detail-campaign-filter"
                className={classes.campaignSelect}
                panelClassName={classes.campaignSelectPanel}
                textFieldProps={{ size: 'small' }}
                popperPlacement="bottom-start"
                onClear={() => onFilterChange({ value: '', type: undefined })}
                isLoading={isCampaignFilterLoading}
                statusOptions={campaignStatusOptions}
                selectedStatusValue={campaignStatusFilter}
                onStatusChange={handleCampaignStatusChange}
                searchByOptions={isAllContext ? campaignSearchByOptions : undefined}
                selectedSearchByValues={isAllContext ? campaignSearchByValues : undefined}
                onSearchByChange={isAllContext ? handleCampaignSearchByChange : undefined}
                smsProviderOptions={smsProviderOptions}
                selectedSmsProviderValue={smsProviderFilter}
                onSmsProviderChange={handleSmsProviderChange}
                promotionalFilterOptions={promotionalFilterOptions}
                selectedPromotionalValue={campaignPromotionalFilter}
                onPromotionalFilterChange={handlePromotionalFilterChange}
                onOpen={handleCampaignDropdownOpen}
                onSearchChange={handleCampaignSearch}
              />
              <NavBar options={navBarOptions[type]} />
            </Grid>
            <div className={classes.providerContainer}>
              <Grid item>
                {queryData.type === 'SMS' && (
                  <ToggleButtonGroup
                    value={queryData.providerType || smsProviderFilter || 'VONAGE'}
                    exclusive
                    className={`${classes.dateTypeToggleGroup} ${classes.outsideDateTypeToggle}`}
                    onChange={handleProviderTypeToggle}>
                    <ToggleButton value="VONAGE">Vonage</ToggleButton>
                    <ToggleButton value="TWILIO">Twilio</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Grid>
              <Grid item className={classes.rightControls}>
                {!isActivityView && (
                  <ToggleButtonGroup
                    value={queryData.dateType || 'DAY'}
                    exclusive
                    className={`${classes.dateTypeToggleGroup} ${classes.outsideDateTypeToggle}`}
                    onChange={handleDateTypeChange}>
                    <ToggleButton value="DAY">Daily</ToggleButton>
                    <ToggleButton value="MONTH">Monthly</ToggleButton>
                  </ToggleButtonGroup>
                )}
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={openDateFilter}>
                  {getDateFilterButtonLabel()}
                </Button>
                <DateFilterPopover
                  isActivityView={isActivityView}
                  dateFilterDraft={dateFilterDraft}
                  rangeSelectionStep={rangeSelectionStep}
                  visibleDailyMonth={visibleDailyMonth}
                  visibleMonthYear={visibleMonthYear}
                  minAvailableDailyDate={getMinAvailableDailyDate()}
                  maxAvailableDailyDate={getMaxAvailableDailyDate()}
                  minAvailableMonthlyDate={getMinAvailableMonthlyDate()}
                  maxAvailableMonthlyDate={getMaxAvailableMonthlyDate()}
                  onDateFilterChange={setDateFilterDraft}
                  onRangeSelectionStepChange={setRangeSelectionStep}
                  onVisibleDailyMonthChange={setVisibleDailyMonth}
                  onVisibleMonthYearChange={setVisibleMonthYear}
                  onApplyDateFilters={applyDateFilters}
                  onCloseDateFilter={closeDateFilter}
                  dateFilterError={dateFilterError}
                  dateType={isActivityView ? 'DAY' : (queryData.dateType || 'DAY')}
                  anchorEl={dateFilterAnchorEl}
                  getPresetRange={getPresetRange}
                  queryData={queryData}
                  dailyReportDelayHours={dailyReportDelayHours}
                    monthlyReportDelayHours={monthlyReportDelayHours}
                    dailyRuntime={dailyRuntime}
                    monthlyRuntime={monthlyRuntime}
                />
              </Grid>
            </div>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent className={classes.content}>
          {activeProviderMetric && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Button
                startIcon={<ArrowBack />}
                onClick={handleBackToDashboard}
                variant="contained"
                color="primary"
                size="medium">
                Back to Dashboard
              </Button>
              <Button
                onClick={handleMetricCsvDownload}
                startIcon={<GetApp />}
                disabled={isDownloading || campaignData.length === 0}
                color="primary"
                variant="outlined">
                {isDownloading ? 'Exporting...' : 'Export'}
              </Button>
            </Box>
          )}

          {isLoading ? (
            <Box>
              <Skeleton height={26} width={280} style={{ marginBottom: 16 }} />
              <Skeleton height={56} style={{ marginBottom: 12 }} />
              <Skeleton height={56} style={{ marginBottom: 12 }} />
              <Skeleton height={56} style={{ marginBottom: 12 }} />
              <Skeleton height={340} />
            </Box>
          ) : emptyMessage ? (
            <Typography className={classes.message}>
              {emptyMessage}
            </Typography>
          ) : queryData.type === 'EMAIL' ? (
            activeProviderMetric ? (
              <Activity
                providerMetric={activeProviderMetric}
                report={report}
              />
            ) : (
              <EmailCampaignDetail
                report={report}
                campaignId={campaignId}
                onStatClick={handleStatClick}
              />
            )
          ) : (
            <StatBoxCard report={report} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Campaign;
